import { presetTemplates } from './presetTemplates';

import type { LayoutTemplate } from './types';

/**
 * LayoutTemplateRegistry
 * Manages layout templates - both preset and custom templates
 * Implements singleton pattern for global access
 */
export class LayoutTemplateRegistry {
  private static instance: LayoutTemplateRegistry | null = null;
  private templates: Map<string, LayoutTemplate> = new Map();

  constructor() {
    // Load preset templates on instantiation
    this.loadPresetTemplates();
  }

  /**
   * Gets the singleton instance
   */
  static getInstance(): LayoutTemplateRegistry {
    if (!LayoutTemplateRegistry.instance) {
      LayoutTemplateRegistry.instance = new LayoutTemplateRegistry();
    }
    return LayoutTemplateRegistry.instance;
  }

  /**
   * Loads preset templates into the registry
   */
  private loadPresetTemplates(): void {
    presetTemplates.forEach(template => {
      this.templates.set(template.id, template);
    });
  }

  /**
   * Registers a new template
   * @throws Error if template with same ID already exists
   * @throws Error if template validation fails
   */
  register(template: LayoutTemplate): void {
    // Validate required properties
    if (!template.id || !template.name || !template.type) {
      throw new Error('Template must have id, name, and type properties');
    }

    // Check for duplicate ID
    if (this.templates.has(template.id)) {
      throw new Error(`Template with ID ${template.id} already exists`);
    }

    // Validate slots match layout elements
    this.validateTemplate(template);

    // Register the template
    this.templates.set(template.id, template);
  }

  /**
   * Validates that template slots match layout element names
   */
  private validateTemplate(template: LayoutTemplate): void {
    const slotSet = new Set(template.slots);
    const elementNames = template.layoutElements.map(el => el.name);
    const elementSet = new Set(elementNames);

    // Check if all slots have corresponding layout elements
    const slotsMatch =
      slotSet.size === elementSet.size &&
      [...slotSet].every(slot => elementSet.has(slot));

    if (!slotsMatch) {
      throw new Error('Template slots and layout element names must match');
    }
  }

  /**
   * Unregisters a template by ID
   * @returns true if template was removed, false if it didn't exist
   */
  unregister(templateId: string): boolean {
    return this.templates.delete(templateId);
  }

  /**
   * Gets a template by ID
   */
  getTemplate(templateId: string): LayoutTemplate | undefined {
    return this.templates.get(templateId);
  }

  /**
   * Gets all registered templates
   */
  getAllTemplates(): LayoutTemplate[] {
    return Array.from(this.templates.values());
  }
}
